import React, { useEffect, useState } from "react";
import { Routes, Route, Outlet, useNavigate, Link, useLocation } from "react-router-dom";
import "./styles.css";
import "./newFunnel.css";
import "./funnel-v2/funnel-v2.css";
import {
  FunnelProvider,
  Step1 as V2Step1,
  Step2 as V2Step2,
  Step3 as V2Step3,
  Step4 as V2Step4,
  Step5 as V2Step5,
  Step6 as V2Step6
} from "./funnel-v2";
import LegalPageLayout from "./funnel-v2/LegalPageLayout";
const BACKEND_ROOT =
  (process.env.REACT_APP_BACKEND_URL || "https://familyhealth-backend.onrender.com").replace(/\/$/, "");
const API = `${BACKEND_ROOT}/api`;

const PHONE_DISPLAY = process.env.REACT_APP_CALL_PHONE || "833-721-2356";
const PHONE_TEL = "+1" + PHONE_DISPLAY.replace(/\D/g, "");

function getTrackingData() {
  const params = new URLSearchParams(window.location.search);

  return {
    utm_source: params.get("utm_source") || "",
    utm_medium: params.get("utm_medium") || "",
    utm_campaign: params.get("utm_campaign") || "",
    utm_term: params.get("utm_term") || "",
    utm_content: params.get("utm_content") || "",
    gclid: params.get("gclid") || "",
    fbclid: params.get("fbclid") || "",
    landing_page: window.location.href,
    referrer: document.referrer || "",
    user_agent: navigator.userAgent || "",
    submitted_at: new Date().toISOString(),
  };
}

const TCPA_CONSENT_TEXT =
  'By clicking "Request My Plan Info," I consent to receive calls, text messages, emails, and prerecorded/artificial voice messages from Family Health Plans, licensed insurance agents, marketing partners, and service providers regarding health insurance options at the phone number I provided. I understand that my consent is not required to make a purchase, message/data rates may apply, and I may opt out at any time.';

const GOVERNMENT_DISCLAIMER =
  "FamilyHealth-Plans.com is not a government website and is not affiliated with Healthcare.gov, Medicare, Medicaid, any federal marketplace, or any state marketplace.";

function trackPage1LeadConversion() {
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("event", "conversion", {
      send_to: "AW-18125368614/DxgLCIGE1aQcEKba7MJD",
      value: 1.0,
      currency: "USD"
    });
  }
}

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth"
    });
  }, [pathname]);

  return null;
}

function formatPhone(value) {
  const digits = value.replace(/\D/g, "").slice(0, 10);

  if (digits.length <= 3) {
    return digits;
  }

  if (digits.length <= 6) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  }

  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

const STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY", "DC"
];

const INCOME_RANGES = [
  "Under $25,000",
  "$25,000 - $49,999",
  "$50,000 - $74,999",
  "$75,000 - $99,999",
  "$100,000 - $149,999",
  "$150,000+"
];

function saveLeadId(id) {
  localStorage.setItem("lead_id", id);
}

function getLeadId() {
  return localStorage.getItem("lead_id");
}

function Header({ hidePhone = false }) {
  const callClick = async () => {
    try {
      await fetch(`${API}/calls`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ page: window.location.pathname, phone: PHONE_DISPLAY })
      });
    } catch {}
  };

  return (
    <header className="site-header">
      <Link to="/" className="brand-link">
        <div className="brand-mark">✓</div>
        <div>
          <div className="brand-name">FamilyHealth Plans</div>
          <div className="brand-tagline">Compare. Save. Get covered.</div>
        </div>
      </Link>

      {!hidePhone && (
        <a className="call-pill" href={`tel:${PHONE_TEL}`} onClick={callClick}>
          <span className="phone-icon">☎</span>
          <span className="call-label">Call Now:</span>
          <span>{PHONE_DISPLAY}</span>
        </a>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer className="footer">
      <div className="footer-inner">
        <div>
          <h3>FamilyHealth Plans</h3>
          <p>
            We help families compare affordable health coverage options and connect with licensed insurance agents.
          </p>
        </div>

        <div className="footer-links">
          <Link to="/privacy-policy">Privacy Policy</Link>
          <Link to="/terms-of-use">Terms of Use</Link>
          <Link to="/consent-to-contact">Consent to Contact</Link>
          <Link to="/do-not-sell">Do Not Sell or Share My Personal Information</Link>
          <Link to="/do-not-call-policy">Do Not Call Policy</Link>
          <Link to="/contact">Contact / Licensing Information</Link>
          <Link to="/admin">Agent Portal</Link>
        </div>
      </div>

      <div className="footer-disclaimer">
        <p>
          <strong>Disclaimer:</strong> FamilyHealth Plans is a private insurance lead and enrollment assistance website.
          We are not HealthCare.gov and we are not affiliated with or endorsed by any government agency or the federal
          Medicare program.
        </p>
        <p>
          To see all available Qualified Health Plan options in your state, visit{" "}
          <a href="https://www.healthcare.gov" target="_blank" rel="noreferrer">HealthCare.gov</a>. Submitting a form
          does not enroll you in coverage. Plans, pricing, benefits, subsidies, and eligibility vary by state,
          household, income, carrier, and product type.
        </p>
      </div>
    </footer>
  );
}

function Shell({ children, hero = false, hideHeaderPhone = false }) {
  return (
    <>
      <Header hidePhone={hideHeaderPhone} />
      <main className={hero ? "main main-hero" : "main"}>{children}</main>
      <Footer />
    </>
  );
}

function Progress({ step, total = 3, labels = ["About You", "Coverage", "Health"] }) {
  const pct = Math.round((step / total) * 100);

  return (
    <div className="progress-box">
      <div className="progress-top">
        <span>STEP {step} OF {total}</span>
        <span>{pct}% complete</span>
      </div>
      <div className="progress-track">
        <div className="progress-fill" style={{ width: `${pct}%` }} />
      </div>
      <div
  className="progress-steps"
  style={{ gridTemplateColumns: `repeat(${labels.length}, 1fr)` }}
>
  {labels.map((label, index) => (
          <div className={index + 1 <= step ? "progress-step active" : "progress-step"} key={label}>
            <div className="progress-dot">{index + 1 < step ? "✓" : index + 1}</div>
            <span>{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function TrustBadges() {
  return (
    <div className="trust-badges">
      <div>🔒 Secure Form</div>
      <div>👤 Licensed Agents</div>
      <div>⭐ Top Carriers</div>
      <div>⏱ Fast Match</div>
    </div>
  );
}

function RadioGroup({ value, onChange, options, columns = 2 }) {
  return (
    <div className={columns === 3 ? "radio-grid three" : "radio-grid"}>
      {options.map((option) => (
        <button
          key={option}
          type="button"
          className={value === option ? "radio-card active" : "radio-card"}
          onClick={() => onChange(option)}
        >
          {option}
        </button>
      ))}
    </div>
  );
}

function Step1() {
  const nav = useNavigate();
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    zipcode: "",
    state: "",
    phone: "",
    annual_income: "",
    age: "",
    consent: false
  });

  const [err, setErr] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const set = (key, value) => {
    setForm({ ...form, [key]: value });
    setErr("");
  };

  async function submit(e) {
    e.preventDefault();
    setErr("");

    const phoneDigits = form.phone.replace(/\D/g, "");
    const ageNumber = Number(form.age);

    if (!form.first_name.trim()) return setErr("Please enter your first name.");
    if (!form.last_name.trim()) return setErr("Please enter your last name.");
    if (!/^\d{5}$/.test(form.zipcode)) return setErr("Please enter a valid 5-digit ZIP code.");
    if (!form.state) return setErr("Please select your state.");
    if (phoneDigits.length < 10) return setErr("Please enter a valid 10-digit phone number.");
    if (!form.annual_income) return setErr("Please select your annual household income.");
    if (!ageNumber || ageNumber < 18 || ageNumber > 99) return setErr("Please enter an age between 18 and 99.");
    if (!form.consent) return setErr("You must agree to be contacted before continuing.");

    const consentText =
  'By clicking "See My Options", I agree to be contacted by FamilyHealth Plans, licensed insurance agents, insurance agencies, marketing partners, call centers, lead buyers, vendors, and service providers at the phone number and email I provided, including by calls, texts, prerecorded/artificial voice messages, and automated dialing systems, about health insurance and related products. Consent is not required to make a purchase. Message and data rates may apply. I can opt out by replying STOP or by asking not to be called.';
    setSubmitting(true);

    try {
  const res = await fetch(`${API}/leads`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      ...form,
      age: ageNumber,

      consent_text: TCPA_CONSENT_TEXT,
      page_url: window.location.href,

      consent_proof: {
        consent_given: true,
        consent_text: TCPA_CONSENT_TEXT,
        consent_timestamp: new Date().toISOString(),
        form_url: window.location.href,
        user_agent: navigator.userAgent || "",
        trustedform_cert_url: "",
        jornaya_lead_id: ""
      },

      tracking: getTrackingData(),

      buyer_routing: {
        buyer_status: "unassigned",
        buyer_name: "",
        buyer_id: "",
        buyer_price: "",
        lead_status: "new",
        lead_type: "form_lead",
        appointment_requested: false,
        appointment_date: "",
        appointment_time: "",
        appointment_timezone: "",
        call_requested: false,
        ringba_call_id: "",
        sold: false,
        sold_at: "",
        revenue: 0
      }
    })
  });
      if (!res.ok) throw new Error("Lead creation failed.");

     const data = await res.json();
saveLeadId(data.lead_id);
trackPage1LeadConversion();
nav("/final-questions");
    } catch (error) {
      setErr("There was a problem saving your information. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
  <Shell hero hideHeaderPhone>
    <section className="hero contact-page">
      <div className="step2-polished-topbar">
        <div className="tp1-logo">
          <span className="tp1-logo-icon">♡</span>
          <div>
            <strong>FamilyHealth</strong>
            <span>Plans</span>
          </div>
        </div>

        <div className="tp1-secure">🔒 Secure & Confidential</div>
      </div>

      <div className="hero-inner contact-card contact-card-centered">
         <div className="hero-copy contact-copy">
            <div className="mini-badge">✓ Open enrollment partners</div>
            <h1>Almost Done — Complete Your Coverage Request</h1>
            <p>Enter your contact details so a licensed representative may follow up about available coverage options.</p>

            <div className="rating-row">
              
              <div>
                <div className="stars">★★★★★</div>
                <small>Trusted by families nationwide</small>
              </div>
            </div>

            <div className="hero-call">✓ Complete your contact details to continue to final questions.</div>
          </div>

          <div className="form-card">
            <Progress
  step={3}
  total={4}
  labels={["Start", "Coverage", "Contact Info", "Final Questions"]}
/>

            <form onSubmit={submit} className="form">
              <div className="grid two">
                <label>
                  First Name
                  <input
                    value={form.first_name}
                    onChange={(e) => set("first_name", e.target.value)}
                    placeholder="Jane"
                  />
                </label>

                <label>
                  Last Name
                  <input
                    value={form.last_name}
                    onChange={(e) => set("last_name", e.target.value)}
                    placeholder="Smith"
                  />
                </label>

                <label>
                  ZIP Code
                  <input
                    inputMode="numeric"
                    maxLength="5"
                    value={form.zipcode}
                    onChange={(e) => set("zipcode", e.target.value.replace(/\D/g, "").slice(0, 5))}
                    placeholder="33304"
                  />
                </label>

                <label>
                  State
                  <select value={form.state} onChange={(e) => set("state", e.target.value)}>
                    <option value="">Select state</option>
                    {STATES.map((state) => <option key={state} value={state}>{state}</option>)}
                  </select>
                </label>
              </div>

              <label>
                Phone Number
                <input
                  type="tel"
                  inputMode="tel"
                  value={form.phone}
                  onChange={(e) => set("phone", formatPhone(e.target.value))}
                  placeholder="(555) 123-4567"
                  maxLength="14"
                />
              </label>

              <div className="grid two">
                <label>
                  Annual Household Income
                  <select value={form.annual_income} onChange={(e) => set("annual_income", e.target.value)}>
                    <option value="">Select range</option>
                    {INCOME_RANGES.map((range) => <option key={range}>{range}</option>)}
                  </select>
                </label>

                <label>
                  Age
                  <input
                    type="number"
                    min="18"
                    max="99"
                    value={form.age}
                    onChange={(e) => set("age", e.target.value)}
                    placeholder="35"
                  />
                </label>
              </div>

              <label className="checkbox-line">
                <input
                  type="checkbox"
                  checked={form.consent}
                  onChange={(e) => set("consent", e.target.checked)}
                />
                <span>I agree to be contacted as described below.</span>
              </label>

              <button className="primary-btn" disabled={submitting}>
 {submitting ? "Saving your info..." : "Continue to Final Questions →"}
</button>

{submitting && (
  <p className="saving-note">
    Please wait while we securely save your information. This may take a few seconds.
  </p>
)}

{err && <p className="error">{err}</p>}

<p className="disclosure">{TCPA_CONSENT_TEXT}</p>
            </form>
          </div>
        </div>
      </section>

      <div className="section-wrap"><TrustBadges /></div>

      <section className="value-grid">
        <div><h3>Top Carriers</h3><p>We work with leading insurance companies to bring you competitive options.</p></div>
        <div><h3>Personalized Quotes</h3><p>Get plan options tailored to your household size, income, and needs.</p></div>
        <div><h3>Real Licensed Agents</h3><p>Speak with licensed agents who can answer your coverage questions.</p></div>
      </section>
    </Shell>
  );
}

function Step2() {
  const nav = useNavigate();
  const leadId = getLeadId();
  const location = useLocation();
const coverageFromUrl = new URLSearchParams(location.search).get("coverage");

  const [form, setForm] = useState({
    coverage_type: coverageFromUrl || "",
    adults: "",
    children: "",
    currently_insured: "",
    current_insurance_provider: "",
    pregnancy_coverage: "",
    substance_abuse_coverage: ""
  });

  const [err, setErr] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const set = (key, value) => {
    setForm({ ...form, [key]: value });
    setErr("");
  };

  async function submit(e) {
    e.preventDefault();
    if (!leadId) return nav("/");

    if (!form.coverage_type || !form.currently_insured || !form.pregnancy_coverage || !form.substance_abuse_coverage) {
      return setErr("Please complete all fields.");
    }

   if (form.currently_insured === "Yes" && !form.current_insurance_provider) {
  return setErr("Please select your current insurance provider.");
}

setSubmitting(true);

    try {
      const res = await fetch(`${API}/leads/${leadId}/step2`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form)
      });

      if (!res.ok) throw new Error("Step 2 failed.");
nav("/contact-info");
    } catch {
      setErr("Could not save. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
  <Shell hero hideHeaderPhone>
    <section className="step2-polished-page">
      <div className="step2-polished-topbar">
        <div className="tp1-logo">
          <span className="tp1-logo-icon">♡</span>
          <div>
            <strong>FamilyHealth</strong>
            <span>Plans</span>
          </div>
        </div>

        <div className="tp1-secure">🔒 Secure & Confidential</div>
      </div>

      <div className="form-card step2-test-card step2-polished-card">
        <Progress
  step={2}
  total={4}
  labels={["Start", "Coverage", "Contact Info", "Final Questions"]}
/>
        <h1 className="page-title">
          Tell us about your <span>Household</span>
        </h1>
        <p className="page-subtitle">
          This helps us match you with the right coverage options.
        </p>

          <form onSubmit={submit} className="form">
            <label>
              Currently Insured?
              <RadioGroup
                value={form.currently_insured}
                onChange={(v) => {
                  set("currently_insured", v);
                  if (v === "No") {
                    setForm((current) => ({
                      ...current,
                      currently_insured: v,
                      current_insurance_provider: ""
                    }));
                  }
                }}
                options={["Yes", "No"]}
              />
            </label>

            {form.currently_insured === "Yes" && (
              <label>
                Current Insurance Provider
                <select
                  value={form.current_insurance_provider}
                  onChange={(e) => set("current_insurance_provider", e.target.value)}
                >
                  <option value="">Select provider</option>
                  <option>Medicare</option>
                  <option>Medicaid</option>
                  <option>Employer Coverage</option>
                  <option>Blue Cross Blue Shield</option>
                  <option>Aetna</option>
                  <option>Humana</option>
                  <option>Cigna</option>
                  <option>MultiPlan</option>
                  <option>First Health</option>
                  <option>UnitedHealthcare</option>
                  <option>Kaiser Permanente</option>
                  <option>Anthem</option>
                  <option>Molina Healthcare</option>
                  <option>Oscar Health</option>
                  <option>Ambetter</option>
                  <option>Centene</option>
                  <option>CareSource</option>
                  <option>Wellcare</option>
                  <option>Other</option>
                </select>
              </label>
            )}

            <label>
              Are you seeking pregnancy coverage?
              <RadioGroup
                value={form.pregnancy_coverage}
                onChange={(v) => set("pregnancy_coverage", v)}
                options={["Yes", "No"]}
              />
            </label>

            <label>
              Are you seeking substance abuse coverage?
              <RadioGroup
                value={form.substance_abuse_coverage}
                onChange={(v) => set("substance_abuse_coverage", v)}
                options={["Yes", "No"]}
              />
            </label>

            <button className="primary-btn" disabled={submitting}>
              {submitting ? "Saving..." : "Continue to Plans →"}
            </button>

            {err && <p className="error">{err}</p>}
                   </form>
        </div>

        <TrustBadges />
      </section>
    </Shell>
  );
}

function Step3() {
  const nav = useNavigate();
  const leadId = getLeadId();

  const [form, setForm] = useState({
    pre_existing_conditions: "",
    pre_existing_conditions_list: [],
    medications: "",
    medication_count: "",
    doctor_visits_per_year: ""
  });

  const [consent, setConsent] = useState(false);
  const [err, setErr] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const set = (key, value) => {
    setForm({ ...form, [key]: value });
    setErr("");
  };

  const toggleCondition = (condition) => {
    setForm((current) => {
      const list = current.pre_existing_conditions_list || [];
      const exists = list.includes(condition);

      return {
        ...current,
        pre_existing_conditions_list: exists
          ? list.filter((item) => item !== condition)
          : [...list, condition]
      };
    });

    setErr("");
  };

  async function submit(e) {
    e.preventDefault();
    if (!leadId) return nav("/");

    if (!form.pre_existing_conditions || !form.medications || !form.doctor_visits_per_year) {
      return setErr("Please complete all fields and agree to be contacted.");
    }

    if (
      form.pre_existing_conditions === "Yes" &&
      (!form.pre_existing_conditions_list || form.pre_existing_conditions_list.length === 0)
    ) {
      return setErr("Please select at least one pre-existing condition.");
    }

    if (form.medications === "Yes" && !form.medication_count) {
      return setErr("Please select how many medications you take.");
    }

    setSubmitting(true);

    try {
      const res = await fetch(`${API}/leads/${leadId}/step3`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          consent
        })
      });

      if (!res.ok) throw new Error("Step 3 failed.");
     nav("/request-received");
    } catch {
      setErr("Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
  <Shell hero hideHeaderPhone>
    <section className="step3-polished-page">
      <div className="step2-polished-topbar">
        <div className="tp1-logo">
          <span className="tp1-logo-icon">♡</span>
          <div>
            <strong>FamilyHealth</strong>
            <span>Plans</span>
          </div>
        </div>

        <div className="tp1-secure">🔒 Secure & Confidential</div>
      </div>

      <div className="form-card step3-polished-card">
          <Progress
  step={4}
  total={4}
  labels={["Start", "Coverage", "Contact Info", "Final Questions"]}
/>
          <h1 className="page-title">
  Final <span>Health Questions</span>
</h1>
         <p className="page-subtitle">
  These final questions help licensed representatives understand your coverage needs.
</p>

          <form onSubmit={submit} className="form">
            <label>
              Any pre-existing conditions?
              <RadioGroup
                value={form.pre_existing_conditions}
                onChange={(v) => {
                  set("pre_existing_conditions", v);
                  if (v === "No") {
                    setForm((current) => ({
                      ...current,
                      pre_existing_conditions: v,
                      pre_existing_conditions_list: []
                    }));
                  }
                }}
                options={["Yes", "No"]}
              />
            </label>

            {form.pre_existing_conditions === "Yes" && (
              <div className="condition-box">
                <div className="condition-title">Select all that apply</div>

                {[
                  "High Blood Pressure",
                  "Diabetes",
                  "Asthma",
                  "COPD",
                  "Heart Disease",
                  "High Cholesterol",
                  "Cancer",
                  "Stroke",
                  "Kidney Disease",
                  "Liver Disease",
                  "Arthritis",
                  "Depression",
                  "Anxiety",
                  "Sleep Apnea",
                  "Thyroid Disorder",
                  "Obesity",
                  "Other"
                ].map((condition) => (
                  <label className="condition-option" key={condition}>
                    <input
                      type="checkbox"
                      checked={(form.pre_existing_conditions_list || []).includes(condition)}
                      onChange={() => toggleCondition(condition)}
                    />
                    <span>{condition}</span>
                  </label>
                ))}
              </div>
            )}

            <label>
              Do you take any medications?
              <RadioGroup
                value={form.medications}
                onChange={(v) => {
                  set("medications", v);
                  if (v === "No") {
                    setForm((current) => ({
                      ...current,
                      medications: v,
                      medication_count: ""
                    }));
                  }
                }}
                options={["No", "Yes"]}
              />
            </label>

            {form.medications === "Yes" && (
              <label>
                How many medications do you take?
                <RadioGroup
                  value={form.medication_count}
                  onChange={(v) => set("medication_count", v)}
                  options={["1-3", "4-6", "7+"]}
                  columns={3}
                />
              </label>
            )}

            <label>
              How often do you visit the doctor per year?
              <RadioGroup
                value={form.doctor_visits_per_year}
                onChange={(v) => set("doctor_visits_per_year", v)}
                options={["0-1", "2-5", "6+"]}
                columns={3}
              />
            </label>

           

            <button className="primary-btn step3-submit-btn" disabled={submitting}>
             {submitting ? "Submitting..." : "Submit My Coverage Request →"}
            </button>

            {err && <p className="error">{err}</p>}
          </form>
        </div>
      </section>
    </Shell>
  );
}

function ThankYou() {
  const [progress, setProgress] = useState(8);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const id = setInterval(() => {
      setProgress((current) => {
        const next = Math.min(current + 11, 100);
        if (next >= 100) {
          clearInterval(id);
          setTimeout(() => setDone(true), 400);
        }
        return next;
      });
    }, 350);

    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const leadId = getLeadId();
    const conversionKey = `google_ads_conversion_${leadId || "request_received"}`;

    if (sessionStorage.getItem(conversionKey)) return;

    if (window.gtag) {
      window.gtag("event", "conversion", {
        send_to: "AW-18125368614/YoL8CO-y17EcEKba7MJD",
        value: 1.0,
        currency: "USD"
      });

      sessionStorage.setItem(conversionKey, "1");
    }
  }, []);

  return (
    <Shell hero hideHeaderPhone>
      <section className="thank-polished-page">
        <div className="step2-polished-topbar">
          <div className="tp1-logo">
            <span className="tp1-logo-icon">♡</span>
            <div>
              <strong>FamilyHealth</strong>
              <span>Plans</span>
            </div>
          </div>

          <div className="tp1-secure">🔒 Secure & Confidential</div>
        </div>

        <div className="thank-card thank-polished-card">
          {!done ? (
            <>
              <div className="spinner thank-spinner">↻</div>
              <h1>Reviewing Your Coverage Request...</h1>
              <p>
                We are preparing your request so a licensed representative may
                follow up about available coverage options.
              </p>

              <div className="progress-track large thank-progress-track">
                <div
                  className="progress-fill thank-progress-fill"
                  style={{ width: `${progress}%` }}
                />
              </div>

              <div className="percent">{Math.round(progress)}%</div>
            </>
          ) : (
            <>
              <div className="success thank-success">✓</div>
              <h1>
                Thank You — Your Request Was <span>Received</span>
              </h1>
              <p>
                A licensed representative may contact you about available
                coverage options. There is no obligation to enroll.
              </p>

              <div className="thank-action-row">
                <a className="thank-secondary-btn" href="/coverage-start">
                  Return Home
                </a>

                <a className="primary-btn thank-call-btn" href={`tel:${PHONE_TEL}`}>
                  ☎ Call Now: {PHONE_DISPLAY}
                </a>
              </div>
            </>
          )}

          {!done && (
            <a className="primary-btn thank-call-btn" href={`tel:${PHONE_TEL}`}>
              ☎ Call Now: {PHONE_DISPLAY}
            </a>
          )}

          <div className="thank-mini-trust">
            <div>
              <span>✓</span>
              <strong>Licensed Agents</strong>
            </div>
            <div>
              <span>🔒</span>
              <strong>Secure Request</strong>
            </div>
            <div>
              <span>✓</span>
              <strong>No Obligation</strong>
            </div>
          </div>

          <p className="thank-disclaimer">
            FamilyHealth Plans is not a government website and is not affiliated
            with Healthcare.gov, Medicare, Medicaid, any federal marketplace, or
            any state marketplace.
          </p>
        </div>
      </section>
    </Shell>
  );
}

function Admin() {
  const [password, setPassword] = useState("");
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  async function load(e) {
    e.preventDefault();
    setErr("");

    const res = await fetch(`${API}/admin/leads`, {
      headers: { "X-Admin-Password": password }
    });

    if (!res.ok) {
      setErr("Wrong password or backend error.");
      return;
    }

    setData(await res.json());
  }

  const leads = data?.leads || [];

  return (
    <Shell>
      <section className="admin-wrap">
        <h1>Lead Dashboard</h1>

        {!data && (
          <form onSubmit={load} className="admin-login">
            <label>
              Password
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
            </label>
            <button className="primary-btn">Sign in</button>
            {err && <p className="error">{err}</p>}
          </form>
        )}

        {data && (
          <>
            <div className="stats">
              <div><b>{data.total}</b><span>Total Leads</span></div>
              <div><b>{leads.filter((lead) => lead.step >= 3).length}</b><span>Completed</span></div>
              <div><b>{leads.filter((lead) => lead.step === 1).length}</b><span>Step 1</span></div>
              <div><b>{leads.filter((lead) => lead.step === 2).length}</b><span>Step 2</span></div>
            </div>

            <div className="table-wrap">
  <table>
    <thead>
      <tr>
        <th>Name</th>
        <th>ZIP</th>
        <th>State</th>
        <th>Phone</th>
        <th>Age</th>
        <th>Income</th>
        <th>Coverage</th>
        <th>Adults</th>
        <th>Children</th>
        <th>Insured</th>
        <th>Provider</th>
        <th>Pregnancy</th>
        <th>Substance Abuse</th>
        <th>Conditions</th>
        <th>Medications</th>
        <th>Doctor Visits</th>
        <th>Step</th>
        <th>Created</th>
      </tr>
    </thead>

    <tbody>
      {leads.length === 0 && (
        <tr>
          <td colSpan="18">No leads yet.</td>
        </tr>
      )}

      {leads.map((lead) => (
        <tr key={lead.lead_id}>
          <td>{`${lead.first_name || ""} ${lead.last_name || ""}`.trim() || "—"}</td>
          <td>{lead.zipcode || "—"}</td>
          <td>{lead.state || "—"}</td>
          <td>{lead.phone || "—"}</td>
          <td>{lead.age || "—"}</td>
          <td>{lead.annual_income || "—"}</td>
          <td>{lead.coverage_type || "—"}</td>
          <td>{lead.adults || "—"}</td>
          <td>{lead.children || "—"}</td>
          <td>{lead.currently_insured || "—"}</td>
          <td>{lead.current_insurance_provider || "—"}</td>
          <td>{lead.pregnancy_coverage || "—"}</td>
          <td>{lead.substance_abuse_coverage || "—"}</td>
          <td>
            {Array.isArray(lead.pre_existing_conditions)
              ? lead.pre_existing_conditions.join(", ")
              : lead.pre_existing_conditions || "—"}
          </td>
          <td>{lead.medications || lead.medication_count || "—"}</td>
          <td>{lead.doctor_visits || "—"}</td>
          <td>{lead.step || "—"}</td>
          <td>{lead.created_at ? new Date(lead.created_at).toLocaleString() : "—"}</td>
        </tr>
      ))}
    </tbody>
  </table>
</div>
          </>
        )}
      </section>
    </Shell>
  );
}

function PrivacyPolicy() {
  return (
    <LegalPageLayout>
        <h1>Privacy Policy</h1>
        <p><strong>Effective Date:</strong> 2026</p>

                <p>
          This Privacy Policy explains how FamilyHealth Plans collects, uses,
          discloses, sells, shares, retains, and protects information submitted
          through this website. FamilyHealth Plans is a private insurance lead and
          enrollment assistance website. We are not HealthCare.gov, a federal or state
          Marketplace, Medicare.gov, CMS, or any other government agency.
        </p>

        <h2>1. Information We Collect</h2>
        <p>
          We may collect information that you voluntarily provide through our website,
          including your first name, last name, phone number, ZIP code, state, age,
          household income range, coverage type, household size, insurance status,
          current insurance provider, pregnancy coverage interest, substance abuse
          coverage interest, pre-existing condition responses, medication responses,
          doctor visit frequency, and other information you submit.
        </p>
        <p>
          We may also automatically collect technical information, including your IP
          address, browser type, device type, operating system, user agent, referring
          website, landing page URL, date and time of submission, advertising source,
          campaign identifiers, click IDs, form version, and consent language shown at
          the time of submission.
        </p>

        <h2>2. Sensitive and Health-Related Information</h2>
        <p>
          Some questions on this website may relate to health status, medications,
          pre-existing conditions, or other personal health-related information. We use
          this information to help match you with insurance options, licensed agents,
          agencies, marketing partners, or service providers. Do not submit information
          you do not want shared for those purposes.
        </p>

        <h2>3. How We Use Your Information</h2>
        <p>We may use your information to:</p>
        <ul>
          <li>Process your request for health insurance information.</li>
          <li>Match you with licensed insurance agents, agencies, call centers, or partners.</li>
          <li>Contact you by phone, text, email, or other communication methods.</li>
          <li>Verify, document, and retain your consent to be contacted.</li>
          <li>Improve our website, advertising, routing, lead quality, and user experience.</li>
          <li>Detect, prevent, and respond to fraud, invalid leads, misuse, or security issues.</li>
          <li>Comply with legal, regulatory, licensing, recordkeeping, and contractual obligations.</li>
        </ul>

        <h2>4. Sharing and Selling of Information</h2>
                <p>
          By submitting information through this website, you understand that FamilyHealth
          Plans may share, transfer, disclose, or sell your information to licensed
          insurance agents, agencies, carriers, marketing partners, lead buyers, call
          centers, technology vendors, CRM providers, compliance vendors, and service
          providers who may contact you about health insurance or related products and
          services.
        </p>
        <p>
          These third parties may use your information to contact you, verify eligibility,
          provide quotes, discuss coverage options, schedule appointments, or offer related
          insurance products. Their use of your information may be governed by their own
          privacy policies and compliance obligations.
        </p>

        <h2>5. Calls, Texts, Emails, and Automated Technology</h2>
                <p>
          If you provide a phone number or email address, you may be contacted by
          FamilyHealth Plans, licensed insurance agents, agencies, marketing partners,
          call centers, lead buyers, vendors, or service providers by phone, text
          message, email, prerecorded or artificial voice message, or automated dialing
          system. Consent is not required to make a purchase. Message and data rates
          may apply.
        </p>

        <h2>6. Consent Records</h2>
        <p>
          We may create and retain proof of consent records, including your submitted
          information, checkbox status, timestamp, IP address, user agent, landing page URL,
          referrer, source, campaign information, consent text shown, and other technical
          data. If we use TrustedForm, Jornaya, or another consent certificate service, we
          may store the certificate or token associated with your submission.
        </p>

        <h2>7. Cookies, Tracking, and Advertising</h2>
        <p>
          We may use cookies, pixels, analytics tools, tracking links, advertising platforms,
          and similar technologies to measure website performance, attribute advertising
          campaigns, prevent fraud, improve user experience, and optimize lead routing.
          These technologies may collect information about your device, browser, visit,
          referral source, and interactions with the site.
        </p>

        <h2>8. Your Opt-Out Rights</h2>
        <p>
          You may opt out of text messages by replying STOP. You may request not to be called
          by asking any caller to place you on their internal do-not-call list. You may also
          contact us through the Contact / Licensing Information page to request removal from
          future communications.
        </p>

        <h2>9. Do Not Sell or Share Requests</h2>
        <p>
          You may request that we not sell or share your personal information by visiting the
          Do Not Sell or Share My Personal Information page. We may need information such as
          your name, phone number, email address, ZIP code, or state to locate your record
          and process your request.
        </p>

        <h2>10. Access, Correction, and Deletion Requests</h2>
        <p>
          You may request access to, correction of, or deletion of personal information that
          we maintain about you. We may need to verify your identity before processing your
          request. Certain information may be retained when required for compliance, fraud
          prevention, dispute resolution, legal obligations, or recordkeeping.
        </p>

        <h2>11. Data Security</h2>
        <p>
          We use reasonable administrative, technical, and physical safeguards designed to
          protect information. However, no website, database, or transmission method is 100%
          secure. You submit information at your own risk.
        </p>

        <h2>12. Data Retention</h2>
        <p>
          We may retain your information for as long as reasonably necessary for the purposes
          described in this policy, including lead routing, customer service, consent proof,
          compliance, dispute resolution, auditing, fraud prevention, and legal obligations.
        </p>

        <h2>13. Children’s Privacy</h2>
        <p>
          This website is intended for adults. We do not knowingly collect information from
          children under 13. If you believe a child submitted information, please contact us
          so we can review and delete the information where appropriate.
        </p>

        <h2>14. Government and Marketplace Disclaimer</h2>
        <p>
          FamilyHealth Plans is a private website. We are not HealthCare.gov, Medicare.gov,
          CMS, a federal Marketplace, a state Marketplace, or any government agency. To view
          all Qualified Health Plan options available in your state, visit HealthCare.gov or
          your state Marketplace.
        </p>

        <h2>15. Plan and Product Disclaimer</h2>
        <p>
          Plans, pricing, benefits, subsidies, provider networks, eligibility, deductibles,
          copays, out-of-pocket costs, and availability vary by state, household, income,
          carrier, and product type. Some plans may not be major medical insurance and may
          not satisfy Affordable Care Act requirements.
        </p>

        <h2>16. Changes to This Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. Changes will be posted on this
          page with an updated effective date. Your continued use of the website after changes
          are posted means you accept the updated policy.
        </p>

        <h2>17. Contact</h2>
        <p>
          For privacy questions, opt-out requests, deletion requests, correction requests, or
          do-not-sell/share requests, please contact us through the Contact / Licensing
          Information page.
        </p>
    </LegalPageLayout>
  );
}

function TermsOfUse() {
  return (
    <LegalPageLayout>
        <h1>Terms of Use</h1>
        <p><strong>Effective Date:</strong> 2026</p>

        <p>
          These Terms of Use govern your access to and use of the FamilyHealth Plans website.
          By using this website or submitting information, you agree to these Terms. If you
          do not agree, do not use the website.
        </p>

        <h2>1. Private Website Disclaimer</h2>
        <p>
          FamilyHealth Plans is a private insurance lead and enrollment assistance website.
          We are not HealthCare.gov, Medicare.gov, CMS, a federal Marketplace, a state
          Marketplace, or any government agency.
        </p>

        <h2>2. No Guarantee of Coverage or Savings</h2>
        <p>
          Submitting information through this website does not guarantee eligibility,
          enrollment, approval, savings, a particular premium, a particular subsidy, or a
          particular plan. Plans, pricing, benefits, availability, networks, and eligibility
          vary by state, household, income, carrier, and product type.
        </p>

        <h2>3. Lead Matching and Third-Party Contacts</h2>
        <p>
          By submitting information, you understand that your information may be matched,
          shared, sold, or transferred to licensed insurance agents, agencies, marketing
          partners, call centers, lead buyers, technology vendors, or service providers who
          may contact you about insurance or related products and services.
        </p>

        <h2>4. No Professional Advice</h2>
        <p>
          Information on this website is provided for general informational and lead matching
          purposes only. It is not legal, tax, medical, financial, or insurance advice. You
          should review all plan documents, exclusions, limitations, premiums, fees, networks,
          deductibles, copays, and out-of-pocket costs before enrolling in any product.
        </p>

        <h2>5. Product Types</h2>
        <p>
          Depending on your location and eligibility, you may be contacted about ACA major
          medical plans, short-term plans, indemnity plans, limited-benefit plans, association
          plans, supplemental products, discount programs, or other health-related products.
          Some products may not be major medical insurance and may not satisfy Affordable Care
          Act requirements.
        </p>

        <h2>6. User Responsibilities</h2>
        <p>
          You agree to provide accurate, current, and complete information. You agree not to
          submit false information, another person’s information without permission, fake phone
          numbers, fraudulent information, or information for unlawful purposes.
        </p>

        <h2>7. Consent to Electronic Communications</h2>
        <p>
          By using the website, you agree that we may provide notices, disclosures, and other
          communications electronically through the website, email, text, or other electronic
          means where allowed by law.
        </p>

        <h2>8. Intellectual Property</h2>
        <p>
          The website, design, text, graphics, logos, forms, and related content are owned by
          or licensed to FamilyHealth Plans or its operators and may not be copied, modified,
          distributed, or used without permission.
        </p>

        <h2>9. Third-Party Websites and Partners</h2>
        <p>
          This website may link to or interact with third-party websites, agents, agencies,
          carriers, vendors, or service providers. We are not responsible for the practices,
          content, privacy policies, or services of third parties.
        </p>

        <h2>10. Limitation of Liability</h2>
               <p>
          To the fullest extent permitted by law, FamilyHealth Plans, its operators,
          owners, officers, employees, contractors, vendors, and partners are not liable
          for indirect, incidental, consequential, special, punitive, or exemplary damages
          arising from your use of the website or interactions with third parties.
        </p>

        <h2>11. Changes to the Terms</h2>
        <p>
          We may update these Terms from time to time. Updated terms will be posted on this
          page. Continued use of the website after updates means you accept the revised Terms.
        </p>

        <h2>12. Contact</h2>
        <p>
          For questions about these Terms, please contact us through the Contact / Licensing
          Information page.
        </p>
    </LegalPageLayout>
  );
}

function ConsentToContact() {
  return (
    <LegalPageLayout>
        <h1>Consent to Contact / TCPA Terms</h1>
        <p><strong>Effective Date:</strong> 2026</p>

        <p>
          This page explains the consent you provide when submitting information through the
          FamilyHealth Plans website.
        </p>

        <h2>1. Consent Language</h2>
                <p>
          By submitting your information, checking the consent box, or clicking a submit button,
          you agree to be contacted by FamilyHealth Plans, licensed insurance agents,
          agencies, marketing partners, call centers, lead buyers, vendors, and service
          providers at the phone number and email address you provided about health insurance
          and related products and services.
        </p>
        <h2>2. Calls, Texts, and Automated Technology</h2>
        <p>
          You agree that contacts may be made by phone call, text message, email, prerecorded
          message, artificial voice message, or automated dialing system. Consent is not required
          to make a purchase. Message and data rates may apply.
        </p>

        <h2>3. Who May Contact You</h2>
                <p>
          You may be contacted by FamilyHealth Plans, licensed insurance agents,
          insurance agencies, lead buyers, marketing partners, call centers, vendors,
          or service providers who receive your information in connection with your request.
        </p>

        <h2>4. Purpose of Contact</h2>
        <p>
          Contacts may relate to health insurance, ACA coverage, private health plans, Medicare
          or supplemental options where applicable, limited-benefit products, appointment setting,
          eligibility review, quote assistance, enrollment assistance, or related products and
          services.
        </p>

        <h2>5. Opting Out</h2>
        <p>
          You may opt out of text messages by replying STOP. You may request not to be called
          by telling any caller to place you on their do-not-call list. You may also submit an
          opt-out request through our Contact / Licensing Information page.
        </p>

        <h2>6. Consent Records</h2>
        <p>
          We may store proof of consent, including your name, phone number, ZIP code, state,
          checkbox status, exact consent text shown, timestamp, IP address, user agent, source,
          landing page URL, referrer, campaign identifiers, and any TrustedForm, Jornaya, or
          similar consent certificate if used.
        </p>

        <h2>7. Marketplace Consent</h2>
        <p>
          If a licensed agent or broker assists you with a Marketplace quote, application,
          update, enrollment, or eligibility review, additional consumer consent may be required
          before accessing or using your Marketplace information.
        </p>

        <h2>8. No Purchase Required</h2>
        <p>
          Your consent to receive marketing calls, texts, or emails is not a condition of
          purchasing any goods or services.
        </p>
    </LegalPageLayout>
  );
}

function DoNotSell() {
  return (
    <LegalPageLayout>
        <h1>Do Not Sell or Share My Personal Information</h1>
        <p><strong>Effective Date:</strong> 2026</p>

        <p>
          FamilyHealth Plans may share, transfer, disclose, or sell personal information to
          licensed insurance agents, agencies, lead buyers, marketing partners, call centers,
          and service providers to help respond to your request for insurance information.
        </p>

        <h2>1. Your Request Rights</h2>
        <p>
          You may request that we not sell or share your personal information. You may also
          request deletion or correction of personal information we maintain about you, subject
          to legal, contractual, fraud prevention, compliance, and recordkeeping exceptions.
        </p>

        <h2>2. How to Submit a Request</h2>
        <p>
          To submit a request, contact us through the Contact / Licensing Information page and
          include enough information for us to locate your record, such as your name, phone
          number, email address, ZIP code, state, and approximate date of submission.
        </p>

        <h2>3. Verification</h2>
        <p>
          We may need to verify your identity before fulfilling a request. If we cannot verify
          your identity or locate your record, we may be unable to complete the request.
        </p>

        <h2>4. Previously Shared Information</h2>
        <p>
          If your information has already been shared with an agent, agency, call center,
          marketing partner, or service provider, you may also need to contact that party
          directly to opt out of their communications or request deletion from their systems.
        </p>

        <h2>5. Internal Suppression</h2>
        <p>
          We may retain limited information, such as your phone number or email address, on an
          internal suppression list to help honor opt-out, do-not-call, or do-not-sell/share
          requests.
        </p>
    </LegalPageLayout>
  );
}

function DoNotCallPolicy() {
  return (
    <LegalPageLayout>
        <h1>Do Not Call Policy</h1>
        <p><strong>Effective Date:</strong> 2026</p>

        <p>
          FamilyHealth Plans respects consumer do-not-call and opt-out requests. This policy
          describes how we handle requests not to receive calls or texts.
        </p>

        <h2>1. Internal Do-Not-Call Requests</h2>
        <p>
          If you ask not to be called, we will make reasonable efforts to add your phone number
          to our internal do-not-call or suppression list. You may make this request by telling
          a caller, replying STOP to text messages, or contacting us through the Contact /
          Licensing Information page.
        </p>

        <h2>2. Text Message Opt-Out</h2>
        <p>
          You may opt out of text messages by replying STOP. If supported, you may reply HELP
          for help information. Message and data rates may apply.
        </p>

        <h2>3. National Do Not Call Registry</h2>
        <p>
          Consumers may register their phone numbers on the National Do Not Call Registry.
          Sellers and telemarketers are responsible for complying with applicable do-not-call
          rules and exemptions.
        </p>

        <h2>4. Third-Party Partners</h2>
        <p>
          If your information was already shared with an agent, agency, call center, lead buyer,
          or marketing partner, you may need to ask that party directly to place you on their
          do-not-call list as well.
        </p>

        <h2>5. Processing Time</h2>
        <p>
          We make reasonable efforts to process do-not-call and opt-out requests promptly, but
          you may receive communications already in progress before your request is fully
          processed.
        </p>

        <h2>6. Recordkeeping</h2>
        <p>
          We may retain limited information, such as phone number, email address, request date,
          and request source, to document and honor your opt-out request.
        </p>
    </LegalPageLayout>
  );
}

function ContactLicensing() {
  return (
    <LegalPageLayout>
        <h1>Contact / Licensing Information</h1>
        <p><strong>Effective Date:</strong> 2026</p>

        <p>
          FamilyHealth-Plans.com is operated for marketing and lead generation
          purposes. The website is not an insurance carrier, insurance agency,
          or government entity.
        </p>

        <p>
          FamilyHealth-Plans.com is a private website. We are not HealthCare.gov,
          Medicare.gov, CMS, a federal Marketplace, a state Marketplace, or any
          government agency.
        </p>

        <h2>Business Information</h2>
        <p><strong>Website:</strong> FamilyHealth-Plans.com</p>
        <p><strong>Marketing Operator:</strong> Caffeine Leads LLC</p>
        <p><strong>Website Contact Email:</strong> support@familyhealth-plans.com</p>

        <h2>Licensing Information</h2>
        <p>
          Information submitted through this website may be shared with licensed
          insurance agents, licensed insurance agencies, marketing partners, call
          centers, lead buyers, vendors, and service providers who may contact you
          about available health insurance or related products.
        </p>

        <p>
          Insurance availability, eligibility, benefits, premiums, exclusions,
          recommendations, applications, and enrollment decisions are handled by
          licensed insurance professionals or authorized partners. Licensing, state
          authorization, carrier appointments, and product availability may vary by
          state, carrier, and product type.
        </p>

        <h2>Consumer Requests</h2>
        <p>
          You may use this contact page for privacy requests, opt-out requests,
          do-not-call requests, do-not-sell/share requests, correction requests,
          deletion requests, licensing questions, or general inquiries.
        </p>

        <h2>Important Marketplace Notice</h2>
        <p>
          If you are seeking all available Qualified Health Plan options in your
          state, visit HealthCare.gov or your state Marketplace. If a licensed agent
          assists you with a Marketplace application or enrollment, additional
          documented consumer consent may be required before your Marketplace
          information is accessed or updated.
        </p>
    </LegalPageLayout>
  );
}
const COVERAGE_CARDS = [
  {
    kind: "internal",
    title: "Private Health Coverage",
    summary:
      "Private coverage may be an option for individuals, families, self-employed workers, contractors, and households looking for coverage outside an employer plan.",
    fit: "People who want to request information from licensed representatives about private coverage options.",
button: "Click for Private Health Quote",
  },
  {
    kind: "internal",
    title: "Short-Term Coverage",
    summary:
      "Short-term coverage may provide temporary health coverage for certain people depending on state availability and eligibility.",
    fit: "People between coverage periods, waiting for other coverage to begin, or looking for temporary options.",
 button: "Click for Short Term Quote",
  },
  {
    kind: "internal",
    title: "Supplemental Coverage",
    summary:
      "Supplemental coverage may help with certain medical costs depending on the type of coverage and policy terms.",
    fit: "People who want additional coverage information beyond major medical coverage.",
 button: "Click for Supplemental Quote",
  },
  {
    kind: "internal",
    title: "Self-Employed / 1099 Coverage",
    summary:
      "Self-employed individuals, contractors, freelancers, and 1099 workers may need to request coverage information outside of employer benefits.",
    fit: "Self-employed households or independent contractors looking for coverage information.",
button: "Click for Self Employed Quote",
  },
  {
    kind: "external",
    title: "Healthcare.gov / Federal Marketplace",
    summary:
      "Healthcare.gov is the federal government website for Marketplace coverage information in many states.",
    fit: "People specifically looking for federal Marketplace information.",
    button: "Visit Healthcare.gov",
    href: "https://www.healthcare.gov/",
  },
  {
    kind: "external",
    title: "Medicare",
    summary:
      "Medicare is a federal health insurance program generally for people age 65 or older and certain younger people with disabilities.",
    fit: "People looking specifically for Medicare information.",
    button: "Visit Medicare.gov",
    href: "https://www.medicare.gov/",
  },
  {
    kind: "external",
    title: "Medicaid",
    summary:
      "Medicaid is a public health coverage program administered by states for eligible individuals and families.",
    fit: "People looking specifically for Medicaid information.",
    button: "Learn About Medicaid",
    href: "https://www.medicaid.gov/",
  },
];

function FamilyCoverageInfo() {
  const privateCards = COVERAGE_CARDS.filter((c) => c.kind === "internal");
  const governmentCards = COVERAGE_CARDS.filter((c) => c.kind === "external");

  return (
   <Shell hero hideHeaderPhone>
      <section className="fci-preview-hero fci-preview-hero-clean">
        <div className="fci-center-bubble">
          <h1 className="fci-h1">
            Health Coverage Information for Individuals and Families
          </h1>


        </div>
      </section>

      <section className="fci-section">
        <div className="fci-section-heading">
          <h2>Private coverage information</h2>
          <p>These options continue to your secure coverage request form.</p>
        </div>

        <div className="fci-grid fci-private-grid">
          {privateCards.map((c) => (
            <article key={c.title} className="fci-card">
              <h3 className="fci-card-title">{c.title}</h3>
              <p className="fci-card-summary">{c.summary}</p>
              <p className="fci-card-fit">
                <span className="fci-card-fit-label">Who it may fit: </span>
                <span>{c.fit}</span>
              </p>

              <Link to="/" className="fci-btn fci-btn-primary">
                {c.button}
              </Link>
            </article>
          ))}
        </div>
      </section>

      <section className="fci-section fci-government-section">
        <div className="fci-section-heading">
          <h2>Official government resources</h2>
          <p>
            These open official government websites in a new tab and do not
            submit your information to FamilyHealth Plans.
          </p>
        </div>

        <div className="fci-grid fci-government-grid">
          {governmentCards.map((c) => (
            <article key={c.title} className="fci-card">
              <h3 className="fci-card-title">{c.title}</h3>
              <p className="fci-card-summary">{c.summary}</p>
              <p className="fci-card-fit">
                <span className="fci-card-fit-label">Who it may fit: </span>
                <span>{c.fit}</span>
              </p>

              <a
                href={c.href}
                target="_blank"
                rel="noopener noreferrer"
                className="fci-btn fci-btn-outline"
              >
                {c.button}
              </a>
            </article>
          ))}
        </div>
      </section>
    </Shell>
  );
}
function TestLandingPage() {
  return (
    <Shell hero hideHeaderPhone>
      <section className="tp1-page">
        <div className="tp1-topbar">
          <div className="tp1-logo">
            <span className="tp1-logo-icon">♡</span>
            <div>
              <strong>FamilyHealth</strong>
              <span>Plans</span>
            </div>
          </div>

          <div className="tp1-secure">🔒 Secure & Confidential</div>
        </div>

        <div className="tp1-card">
          <div className="tp1-left">
            <div className="tp1-progress-row">
              <div className="tp1-step-circle">1</div>
              <div>
                <div className="tp1-step-text">Step 1 of 4 • Start</div>
                <div className="tp1-progress-bar">
                  <span></span>
                </div>
              </div>
            </div>

            <h1>
              Find Affordable <span>Health Coverage</span>
            </h1>

            <p className="tp1-subtitle">
              Choose the option that fits your needs and continue to the secure
              coverage request form.
            </p>

            <div className="tp1-question">Who needs coverage?</div>

            <div className="tp1-choice-row">
              <Link to="/coverage-questions?coverage=Individual" className="tp1-choice-card">
                <div className="tp1-choice-icon">I</div>
                <strong>Individual</strong>
              </Link>

              <Link to="/household-info?coverage=Family" className="tp1-choice-card">
                <div className="tp1-choice-icon tp1-green">F</div>
                <strong>Family</strong>
              </Link>
            </div>

            <div className="tp1-disclosure-box">
              <p>
                <strong>✓ Complete Step 1 to continue to your coverage questions.</strong>
              </p>
              <p>
                By continuing, you can request information about available health
                coverage options from licensed representatives. There is no
                obligation to enroll.
              </p>
              <p>
                FamilyHealth Plans is not a government website and is not affiliated
                with Healthcare.gov, Medicare, Medicaid, any federal marketplace, or
                any state marketplace.
              </p>
            </div>
          </div>

          <div className="tp1-photo"></div>
        </div>

        <section className="tp1-trust-row">
          <div>
            <span>✓</span>
            <strong>Licensed Agents</strong>
            <p>You can request information from licensed representatives.</p>
          </div>

          <div>
            <span>✓</span>
            <strong>Coverage Options</strong>
            <p>Explore coverage paths that may fit your needs.</p>
          </div>

          <div>
            <span>🔒</span>
            <strong>Secure Form</strong>
            <p>Your information is submitted through a secure request form.</p>
          </div>

          <div>
            <span>🎧</span>
            <strong>Helpful Support</strong>
            <p>Representatives may contact you about available options.</p>
          </div>
        </section>
      </section>
    </Shell>
  );
}
function TestPage2() {
  return (
    <Shell hero hideHeaderPhone>
      <section className="tp2-page">
        <div className="tp2-topbar">
          <div className="tp2-logo">
            <span className="tp2-logo-icon">♡</span>
            <div>
              <strong>FamilyHealth</strong>
              <span>Plans</span>
            </div>
          </div>

          <div className="tp2-secure">🔒 Secure & Confidential</div>
        </div>

        <div className="tp2-card">
          <div className="tp2-left">
            <div className="tp2-progress-row">
              <div className="tp2-step-circle">2</div>
              <div>
                <div className="tp2-step-text">Step 2 of 4 • Household</div>
                <div className="tp2-progress-bar">
                  <span></span>
                </div>
              </div>
            </div>

            <h1>
              How many <span>adults</span> and <span>children</span> are in the
              household?
            </h1>

            <p className="tp2-subtitle">
              This helps licensed representatives understand the type of
              coverage information your household may need.
            </p>

            <div className="tp2-field-grid">
              <div className="tp2-field-card">
                <div className="tp2-field-icon">A</div>
                <label htmlFor="testAdults">Adults</label>
                <select id="testAdults" defaultValue="">
                  <option value="" disabled>
                    Select adults
                  </option>
                  <option>1 Adult</option>
                  <option>2 Adults</option>
                  <option>3 Adults</option>
                  <option>4+ Adults</option>
                </select>
              </div>

              <div className="tp2-field-card">
                <div className="tp2-field-icon tp2-green">C</div>
                <label htmlFor="testChildren">Children</label>
                <select id="testChildren" defaultValue="">
                  <option value="" disabled>
                    Select children
                  </option>
                  <option>0 Children</option>
                  <option>1 Child</option>
                  <option>2 Children</option>
                  <option>3 Children</option>
                  <option>4+ Children</option>
                </select>
              </div>
            </div>

            <Link to="/coverage-questions?coverage=Family" className="tp2-continue">
              Continue <span>→</span>
            </Link>

            <p className="tp2-note">
              🛡 No obligation to enroll. Your information is submitted through
              a secure request form.
            </p>
          </div>

          <div className="tp2-photo"></div>
        </div>

        <section className="tp2-trust-row">
          <div>
            <span>✓</span>
            <strong>Licensed Agents</strong>
            <p>You can request information from licensed representatives.</p>
          </div>

          <div>
            <span>✓</span>
            <strong>Coverage Options</strong>
            <p>Explore coverage paths that may fit your needs.</p>
          </div>

          <div>
            <span>🔒</span>
            <strong>Secure Form</strong>
            <p>Your information is submitted through a secure request form.</p>
          </div>

          <div>
            <span>🎧</span>
            <strong>Helpful Support</strong>
            <p>Representatives may contact you about available options.</p>
          </div>
        </section>
      </section>
    </Shell>
  );
}
function App() {
  return (
    <>
      <ScrollToTop />
      <Routes>
    <Route element={<FunnelProvider><Outlet /></FunnelProvider>}>
      <Route path="/v2" element={<V2Step1 />} />
      <Route path="/v2/step-1" element={<V2Step1 />} />
      <Route path="/v2/step-2" element={<V2Step2 />} />
      <Route path="/v2/step-3" element={<V2Step3 />} />
      <Route path="/v2/step-4" element={<V2Step4 />} />
      <Route path="/v2/step-5" element={<V2Step5 />} />
      <Route path="/v2/step-6" element={<V2Step6 />} />
    </Route>
       <Route path="/" element={<Step1 />} />
<Route path="/step-2" element={<Step2 />} />
<Route path="/step-3" element={<Step3 />} />
<Route path="/thank-you" element={<ThankYou />} />
<Route path="/family-coverage-info" element={<FamilyCoverageInfo />} />
<Route path="/test" element={<TestLandingPage />} />
  <Route path="/test-page-2" element={<TestPage2 />} />
<Route path="/admin" element={<Admin />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfUse />} />

        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-use" element={<TermsOfUse />} />
        <Route path="/consent-to-contact" element={<ConsentToContact />} />
        <Route path="/do-not-sell" element={<DoNotSell />} />
        <Route path="/do-not-call-policy" element={<DoNotCallPolicy />} />
        <Route path="/contact" element={<ContactLicensing />} />
  <Route path="/coverage-start" element={<TestLandingPage />} />
<Route path="/household-info" element={<TestPage2 />} />
<Route path="/coverage-questions" element={<Step2 />} />
<Route path="/contact-info" element={<Step1 />} />
<Route path="/final-questions" element={<Step3 />} />
<Route path="/request-received" element={<ThankYou />} />
      </Routes>
    </>
  );
}

export default App;
